/**
 * M3 WORLD B6: objective framework. Pure, deterministic, engine-agnostic (no
 * Three.js, no Math.random, no Date.now, no DOM). The client emits typed
 * ObjectiveEvents from existing loop sites (docking, scoop, drop-in, kill, rescue)
 * and steps each Objective with stepObjective; completed objectives pay out via the
 * existing economy credit(wallet, reward). Objectives are surfaced in a "Tasks" tab
 * (hud/servicesMenu.ts) + a one-line HUD readout.
 *
 * B7: the FIRST objective is a scripted, non-combat `investigate` task that targets
 * the story signal (makeStorySignal) — teaching target -> supercruise -> DROP ->
 * drop-in -> inspect -> return. Per the bible's THEME 4 ("verification runs ... have
 * no combat ... they are simply the work"), the opener pays out quietly.
 */

/** Objective kinds. Map to the loop events that advance them. */
export type ObjectiveKind =
  | 'investigate' // drop in at a flagged signal (B7 opener)
  | 'rescue' // protect a distress victim (RESCUED)
  | 'salvage' // scoop N units of cargo (SCOOPED)
  | 'bounty' // destroy N hostiles (KILLED_NPC)
  | 'deliver'; // dock at a target station (DOCKED_AT)

export type ObjectiveStatus = 'ACTIVE' | 'COMPLETE' | 'FAILED';

export interface Objective {
  id: string;
  kind: ObjectiveKind;
  status: ObjectiveStatus;
  target: string; // signal id / station id / npc faction / '' — kind-dependent
  progress: number; // current count
  goal: number; // count required to complete (1 for one-shot kinds)
  reward: number; // credits paid on completion (via economy credit())
  briefing: string; // one-line player-facing text (B-NARRATIVE flavored)
}

/** Typed events emitted from existing client loop sites. */
export type ObjectiveEvent =
  | { type: 'DOCKED_AT'; stationId: string }
  | { type: 'SCOOPED'; qty: number }
  | { type: 'DROPPED_AT_SIGNAL'; signalId: string }
  | { type: 'KILLED_NPC'; faction: string }
  | { type: 'RESCUED'; signalId: string }
  | { type: 'VERIFIED'; signalId: string }; // AAA-01: the Nameswriter VERIFY verb

/** Which event type advances which objective kind. */
const advancesKind = (kind: ObjectiveKind, ev: ObjectiveEvent): boolean => {
  switch (kind) {
    case 'investigate':
      // The opener completes by dropping in OR by performing the VERIFY verb at it.
      return ev.type === 'DROPPED_AT_SIGNAL' || ev.type === 'VERIFIED';
    case 'rescue':
      return ev.type === 'RESCUED';
    case 'salvage':
      return ev.type === 'SCOOPED';
    case 'bounty':
      return ev.type === 'KILLED_NPC';
    case 'deliver':
      return ev.type === 'DOCKED_AT';
  }
};

/** Does this event's target match the objective's target? ('' = any). */
const targetMatches = (obj: Objective, ev: ObjectiveEvent): boolean => {
  if (obj.target === '') return true;
  switch (ev.type) {
    case 'DOCKED_AT':
      return ev.stationId === obj.target;
    case 'DROPPED_AT_SIGNAL':
    case 'RESCUED':
    case 'VERIFIED':
      return ev.signalId === obj.target;
    case 'KILLED_NPC':
      return ev.faction === obj.target;
    case 'SCOOPED':
      return true;
  }
};

/** Increment this objective contributes for an event (SCOOPED carries qty). */
const incrementFor = (ev: ObjectiveEvent): number => (ev.type === 'SCOOPED' ? ev.qty : 1);

/**
 * Advance one objective by one event. Pure: returns a NEW Objective. Only ACTIVE
 * objectives change; a matching event bumps progress and flips to COMPLETE once
 * progress >= goal. Non-matching events are a no-op (return the same reference).
 */
export const stepObjective = (obj: Objective, ev: ObjectiveEvent): Objective => {
  if (obj.status !== 'ACTIVE') return obj;
  if (!advancesKind(obj.kind, ev) || !targetMatches(obj, ev)) return obj;
  const progress = Math.min(obj.goal, obj.progress + incrementFor(ev));
  const status: ObjectiveStatus = progress >= obj.goal ? 'COMPLETE' : 'ACTIVE';
  return { ...obj, progress, status };
};

/** Mark an active objective FAILED (e.g. a distress victim died). Pure. */
export const failObjective = (obj: Objective): Objective =>
  obj.status === 'ACTIVE' ? { ...obj, status: 'FAILED' } : obj;

/** True once an objective has paid out / closed (terminal). */
export const objectiveClosed = (obj: Objective): boolean => obj.status !== 'ACTIVE';

/**
 * B7: the deterministic opening objective — investigate the story signal. Pulls
 * the Cenotaph / Forty-Seven Seconds framing (THEME 4: the work, no combat).
 * @param storySignalId the id returned by signal.makeStorySignal (story-cenotaph-vigil)
 */
export const makeStoryObjective = (storySignalId: string): Objective => ({
  id: 'obj-opening-verify',
  kind: 'investigate',
  status: 'ACTIVE',
  target: storySignalId,
  progress: 0,
  goal: 1,
  reward: 250,
  briefing:
    'VERIFY THE CENOTAPH WAYPOINT — fly to the flagged signal near Mars, drop in, and inspect. Carve true.',
});

/**
 * Build the standard starter objective set for a fresh game: the scripted opener
 * plus two evergreen tasks (salvage + bounty) so the Tasks tab is never empty.
 * Deterministic — no Math.random, no clocks.
 */
export const makeObjectives = (storySignalId: string): Objective[] => [
  makeStoryObjective(storySignalId),
  {
    id: 'obj-salvage-10',
    kind: 'salvage',
    status: 'ACTIVE',
    target: '',
    progress: 0,
    goal: 10,
    reward: 400,
    briefing: 'SCAVENGER — scoop 10 units of salvage from wreckfields and derelicts.',
  },
  {
    id: 'obj-bounty-3',
    kind: 'bounty',
    status: 'ACTIVE',
    target: '',
    progress: 0,
    goal: 3,
    reward: 600,
    briefing: 'CLEAR THE LANES — destroy 3 hostiles preying on signal sources.',
  },
];

/** One-line HUD readout for the active objective (kind + progress + briefing). */
export const objectiveHudLine = (obj: Objective | null): string | null => {
  if (!obj) return null;
  const tag = obj.goal > 1 ? ` (${obj.progress}/${obj.goal})` : '';
  return `TASK: ${obj.briefing}${tag}`;
};
